import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, BookOpen, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AddObituaryModal from '@/components/AddObituaryModal';
import ContributeModal from '@/components/ContributeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ObituariesPage({ user }) {
  const navigate = useNavigate();
  const [obituaries, setObituaries] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedObituary, setSelectedObituary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [obituariesRes, membersRes] = await Promise.all([
        axios.get(`${API}/obituaries`, { withCredentials: true }),
        axios.get(`${API}/members`, { withCredentials: true })
      ]);
      setObituaries(obituariesRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load obituaries');
    } finally {
      setLoading(false);
    }
  };

  const handleAddObituary = async (obituaryData) => {
    try {
      await axios.post(`${API}/obituaries`, obituaryData, {
        withCredentials: true
      });
      toast.success('Obituary created successfully');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to create obituary');
    }
  };

  const handleContribute = async (obituaryId, contribution) => {
    try {
      await axios.put(`${API}/obituaries/${obituaryId}/contribute`, contribution, {
        withCredentials: true
      });
      toast.success('Contribution added successfully');
      fetchData();
      setSelectedObituary(null);
    } catch (error) {
      toast.error('Failed to add contribution');
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.member_id === memberId);
    return member ? member.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
          className="mb-6 text-foreground-muted hover:text-primary"
        >
          <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-serif tracking-tight text-foreground mb-2">Obituaries</h1>
            <p className="text-base font-sans text-foreground-muted">
              Honoring the lives of our loved ones
            </p>
          </div>

          <Button
            onClick={() => setShowAddModal(true)}
            data-testid="add-obituary-btn"
            className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif tracking-wide"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Write Obituary
          </Button>
        </div>

        {obituaries.length === 0 ? (
          <div className="bg-white border border-border shadow-card rounded-lg p-12 text-center">
            <BookOpen className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-serif text-foreground mb-2">No Obituaries Yet</h3>
            <p className="text-base font-sans text-foreground-muted mb-6">
              Create an obituary to honor and remember a loved one
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              data-testid="empty-add-obituary-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif tracking-wide"
            >
              Write First Obituary
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {obituaries.map((obituary, index) => (
              <div 
                key={obituary.obituary_id} 
                className="bg-white border border-border shadow-card rounded-lg p-6"
                data-testid={`obituary-card-${index}`}
              >
                <div className="mb-4">
                  <h2 className="text-2xl font-serif text-foreground mb-2" data-testid="obituary-member-name">
                    In Memory of {getMemberName(obituary.member_id)}
                  </h2>
                  <p className="text-sm font-sans text-foreground-muted">
                    Written by {obituary.author_name}
                  </p>
                </div>

                <div className="prose max-w-none mb-6">
                  <p className="text-base font-sans text-foreground-muted leading-relaxed whitespace-pre-wrap" data-testid="obituary-content">
                    {obituary.content}
                  </p>
                </div>

                {/* Contributions */}
                {obituary.contributions && obituary.contributions.length > 0 && (
                  <div className="border-t border-border pt-4 mt-4">
                    <h3 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-3">Contributions</h3>
                    <div className="space-y-3">
                      {obituary.contributions.map((contrib, idx) => (
                        <div key={idx} className="bg-paper p-4 rounded-lg" data-testid={`contribution-${idx}`}>
                          <p className="text-sm font-sans text-foreground mb-2">
                            {contrib.content}
                          </p>
                          <p className="text-xs font-sans text-muted-foreground">
                            — {contrib.user_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedObituary(obituary)}
                    data-testid="contribute-btn"
                    className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif tracking-wide"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Add Your Memory
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddObituaryModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddObituary}
          members={members}
        />
      )}

      {selectedObituary && (
        <ContributeModal
          obituary={selectedObituary}
          memberName={getMemberName(selectedObituary.member_id)}
          onClose={() => setSelectedObituary(null)}
          onSubmit={(contribution) => handleContribute(selectedObituary.obituary_id, contribution)}
        />
      )}
    </div>
  );
}